import { Color } from '@persona-id/design-tokens';
import { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import {
  ChangeEvent,
  EffectCallback,
  FocusEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import styled from 'styled-components';

import TextArea from 'design-system/components/TextArea';

const StyledTextArea = styled(TextArea)`
  transition: height 0s;

  &:read-only {
    background-color: ${Color.BackgroundInputDisabled};
    border-color: ${Color.BorderInputDisabled};
    color: ${Color.TextDisabled};
  }
`;

/**
 * This hook runs the callback when the referenced element comes into view
 */
const useVisibleEffect = (callback: EffectCallback, ref: MutableRefObject<HTMLElement>) => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback();
          observer.unobserve(entry.target);
        }
      });
    });
    observer.observe(ref.current);
  }, [ref, callback]);
};

/** The `TextareaWidget` is a widget for rendering input fields as textarea.
 *
 * @param props - The `WidgetProps` for this component
 */
export function TextareaWidget<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any,
>({
  id,
  options = {},
  placeholder,
  value,
  required,
  disabled,
  readonly,
  autofocus = false,
  onChange,
  onBlur,
  onFocus,
}: WidgetProps<T, S, F>) {
  const ref = useRef<HTMLTextAreaElement>();

  // NOTE: normally I would highly recommend against editing DOM elements imperatively like this,
  // but this is the best way to enable our textarea to dynamically resize based off of content.
  const autoResize = useCallback(() => {
    const textArea = ref.current;
    // reset the height to auto to shrink the textarea
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    textArea.style.height = 'auto';
    // set the height to fit with a minimum height of 300px;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    textArea.style.height = `${Math.max(textArea.scrollHeight + 2, 200)}px`;
  }, []);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  useVisibleEffect(autoResize, ref);

  useEffect(autoResize, [value, autoResize]);

  const handleChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
      autoResize();
      onChange(value === '' ? options.emptyValue : value);
    },
    [onChange, options.emptyValue, autoResize],
  );

  const handleBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLTextAreaElement>) => onBlur(id, value),
    [onBlur, id],
  );

  const handleFocus = useCallback(
    ({ target: { value } }: FocusEvent<HTMLTextAreaElement>) => onFocus(id, value),
    [id, onFocus],
  );

  return (
    <StyledTextArea
      id={id}
      name={id}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ref={ref}
      value={value ? value : ''}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readonly}
      autoFocus={autofocus}
      rows={options.rows}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onChange={handleChange}
    />
  );
}
